import { type FetchResult } from '@apollo/client';
import {
  Breakpoints,
  MINUTE,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import React, { type FC, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  IntroCallInterest,
  LelandErrorCode,
  type Scalars,
} from '../../../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../../../context/AuthContext';
import { trackFirstMessageSent } from '../../../../utils/message';
import { wasMutationSuccessful } from '../../../../utils/mutations';
import {
  trackCoachingSessionScheduled,
  trackIntroCallScheduled,
} from '../../../../utils/session';
import { Radio, Textarea } from '../../../forms/Inputs';
import { ScheduleModalStep, SessionOption } from '../ScheduleModal';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import {
  type ScheduleMessageModalCoachFragment,
  type ScheduleMessageModalCoachingSessionFragment,
  type ScheduleMessageModalCreateCoachingSessionByApplicantMutationVariables,
  type ScheduleMessageModalRequestIntroMessageMutationVariables,
  useScheduleMessageModalAssignPendingOrderMutation,
  useScheduleMessageModalCreateCoachingSessionByApplicantMutation,
  useScheduleMessageModalRequestIntroMessageMutation,
  useScheduleMessageModalUpdateCoachingSessionMutation,
} from './__generated-gql-types__/ScheduleMessageModal.generated';

interface ScheduleMessageModalProps
  extends BaseScheduleSubmissionModalLayoutProps {
  coach: ScheduleMessageModalCoachFragment;
  hasUnlimitedBookingPower: boolean;
  selectedTime: Possible<number>;
  sessionLength: number;
  setOpen: (open: boolean) => void;
  setSelectedTime: (time: Possible<number>) => void;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  coachingSession?: Possible<ScheduleMessageModalCoachingSessionFragment>;
  onSchedule?: (session: {
    id?: Scalars['CoachingSessionUrn']['output'];
    startAt?: number;
  }) => void;
  setStep: (step: ScheduleModalStep) => void;
  sessionType: SessionOption;
  offeringPackageIds: string[];
  isPayAsYouGo: boolean;
  orderToAssign?: string;
}

const INTRO_ERROR_CODES = [
  LelandErrorCode.TOO_MANY_INTRO_CALLS_WITH_COACH,
  LelandErrorCode.TOO_MANY_COACH_REQUESTS,
];

interface IntroCallInterestOptionProps {
  title: string;
  subtitle?: string;
}

const IntroCallInterestOption: FC<IntroCallInterestOptionProps> = ({
  title,
  subtitle,
}) => {
  return (
    <div className="mt-4">
      <span>
        {title}
        {subtitle ? ' -' : ''}
      </span>
      {subtitle ? <span className="">&nbsp;{subtitle}</span> : null}
    </div>
  );
};

const INTRO_INTERESTS_OPTIONS = [
  {
    label: (
      <IntroCallInterestOption
        title="Hourly coaching"
        subtitle="Pay for time as you need it"
      />
    ),
    value: IntroCallInterest.HOURLY,
  },
  {
    label: (
      <IntroCallInterestOption
        title="Package coaching"
        subtitle="Pay for a bundle of coaching hours / services"
      />
    ),
    value: IntroCallInterest.PACKAGE,
  },
  {
    label: <IntroCallInterestOption title="Not sure yet" />,
    value: IntroCallInterest.UNDECIDED,
  },
];

export const ScheduleMessageModal: FC<ScheduleMessageModalProps> = ({
  categoryUrn,
  coach,
  coachingSession,
  hasUnlimitedBookingPower,
  isPayAsYouGo,
  offeringPackageIds,
  onSchedule,
  order,
  selectedTime,
  sessionLength,
  sessionType,
  setOpen,
  setSelectedTime,
  setStep,
  orderToAssign,
  ...baseScheduleModalProps
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);
  const { currentUser, isNewSubscriptionExperience } = useAuth();
  const router = useRouter();

  const isNewExperience = isNewSubscriptionExperience || !!coach.newExperience;

  const isRescheduling = !!coachingSession;

  const [submitError, setSubmitError] = useState<Possible<string>>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ScheduleMessageModalCreateCoachingSessionByApplicantMutationVariables>();

  const {
    register: registerIntroCall,
    getValues,
    watch,
  } = useForm<ScheduleMessageModalRequestIntroMessageMutationVariables>();

  const [createCoachingSessionByApplicantAction] =
    useScheduleMessageModalCreateCoachingSessionByApplicantMutation();
  const [updateCoachingSession] =
    useScheduleMessageModalUpdateCoachingSessionMutation();
  const [requestIntroMessageAction] =
    useScheduleMessageModalRequestIntroMessageMutation();
  const [assignPendingOrderAction] =
    useScheduleMessageModalAssignPendingOrderMutation();

  const isIntroCall = sessionType === SessionOption.INTRO_CALL;
  const watchInterest = watch('interest');

  const formatCoachingSession = (time: number, title: string) => {
    return {
      startAt: time,
      endAt: time + sessionLength * MINUTE,
      oneOnOneSession: {
        title,
      },
    };
  };

  const formatCoachingSessionTitle = () =>
    `${coach.user.firstName} <> ${currentUser?.firstName} (Leland ${
      isIntroCall ? 'Intro Call' : 'Coaching Session'
    })`;

  const handleNonIntroSessionScheduling = async (
    time: number,
    title: string,
    message: Possible<string>,
  ) => {
    let res: FetchResult;
    const session: {
      id?: Scalars['CoachingSessionUrn']['output'];
      startAt?: number;
    } = {};

    if (isRescheduling) {
      res = await updateCoachingSession({
        variables: {
          id: coachingSession.id,
          input: formatCoachingSession(time, title),
          message: message,
        },
      });
      session.id = coachingSession.id;
      session.startAt = time;
    } else {
      const coachingSessionRes = await createCoachingSessionByApplicantAction({
        variables: {
          createCoachingSessionByApplicantInput: {
            coachUrn: coach.id,
            ...formatCoachingSession(time, title),
          },
          categoryUrn,
          message: message,
        },
      });

      const createdSession =
        coachingSessionRes.data?.createCoachingSessionByApplicant;
      if (createdSession) {
        trackCoachingSessionScheduled({
          attendees: (createdSession.attendees ?? []).map(
            (attendee) => attendee.id,
          ),
          coachUrn: coach.id,
          sessionUrn: createdSession.id,
          startAt: createdSession.startAt,
        });
        session.id = createdSession.id;
        session.startAt = createdSession.startAt;
      }

      res = coachingSessionRes;
    }

    if (!wasMutationSuccessful(res)) {
      setSubmitError(res.errors?.[0].extensions?.message as Optional<string>);
    } else {
      onSchedule?.(session);
      setStep(ScheduleModalStep.SUCCESS);
    }
  };

  const onSubmitWrapper: SubmitHandler<
    ScheduleMessageModalCreateCoachingSessionByApplicantMutationVariables
  > = async ({ message }) => {
    if (isIntroCall && !isRescheduling) {
      const interest = getValues('interest');
      const coachingSession = selectedTime
        ? {
            coachUrn: coach.id,
            ...formatCoachingSession(
              selectedTime,
              formatCoachingSessionTitle(),
            ),
          }
        : undefined;
      const introCallRes = await requestIntroMessageAction({
        variables: {
          coachUrn: coach.id,
          message: message ?? `Hi! I'd like to schedule an intro session.`,
          categoryUrn: categoryUrn,
          interest:
            (isNewExperience
              ? isPayAsYouGo
                ? IntroCallInterest.HOURLY
                : offeringPackageIds?.length
                  ? IntroCallInterest.PACKAGE
                  : IntroCallInterest.UNDECIDED
              : interest) ?? IntroCallInterest.UNDECIDED,
          introCallRequested: !!selectedTime,
          coachingSession: coachingSession,
          packageUrns: isNewExperience ? offeringPackageIds : undefined,
        },
      });

      if (!wasMutationSuccessful(introCallRes)) {
        setSubmitError(
          (INTRO_ERROR_CODES.includes(
            introCallRes.errors?.[0].extensions?.errorCode as LelandErrorCode,
          ) &&
            (introCallRes.errors?.[0].extensions
              ?.message as Optional<string>)) ||
            'Error setting up intro session. Please try again. If the problem persists, please contact support.',
        );
        return;
      } else {
        const scheduledSession =
          introCallRes.data?.requestIntroMessage?.coachingSessions?.[0];
        if (scheduledSession) {
          trackIntroCallScheduled({
            coachUrn: coach.id,
            sessionUrn: scheduledSession.id,
            startAt: scheduledSession.startAt,
            createdAt: scheduledSession.createdAt,
          });
          trackFirstMessageSent({
            coachUrn: coach.id,
          });
        }
        onSchedule?.(scheduledSession ?? {});
        setStep(ScheduleModalStep.SUCCESS);
      }
    } else {
      if (selectedTime) {
        if (orderToAssign) {
          await assignPendingOrderAction({
            variables: {
              coachUrn: coach.id,
              orderUrn: orderToAssign,
            },
          });
        }
        await handleNonIntroSessionScheduling(
          selectedTime,
          isRescheduling &&
            coachingSession.content.__typename === 'OneOnOneSession'
            ? coachingSession.content.title
            : formatCoachingSessionTitle(),
          message,
        );
      } else {
        setOpen(false);
        setStep(ScheduleModalStep.CALENDAR);
        await router.push('/inbox');
      }
    }
  };

  /* 
    This is a workaround for mobile safari to prevent the page from scrolling after the virtual keyboard is opened. 
    On mobile it will essentially override scroll to keep under scroll from happening.
    When the component unmounts we remove the listeners.
  */
  useEffect(() => {
    if (isMd) {
      return;
    }
    const visualViewport = window.visualViewport;
    let offset = 0;
    let viewportWidth = window.innerWidth;
    let viewportHeight = window.innerHeight;

    const handleScrollToTop = () => {
      window.scrollTo(0, 0);
    };

    const handleResize = (event: Event) => {
      const $target = event?.target as unknown as HTMLDivElement;
      const $page = document.body;

      if (viewportWidth !== parseInt($target.style.width)) {
        viewportWidth = window.innerWidth;
        viewportHeight = window.innerHeight;
      }

      if (viewportHeight - parseInt($target.style.height) > 150) {
        handleScrollToTop();
        const adjustment =
          viewportHeight - parseInt($target.style.height) - offset;
        $page.style.bottom = `${adjustment}px`;
      } else if (
        viewportHeight === parseInt($target.style.height) ||
        viewportHeight - parseInt($target.style.height) <= 150
      ) {
        offset = viewportHeight - parseInt($target.style.height);
        $page.style.bottom = '0px';
      }
    };

    // only do this if it's mobile size
    // Check for visual viewport to handle resizing
    if (visualViewport) {
      visualViewport.addEventListener('resize', handleResize);
    }

    document.addEventListener('touchend', handleScrollToTop);

    return () => {
      document.removeEventListener('touchend', handleScrollToTop);
      visualViewport?.removeEventListener('resize', handleResize);
    };
  }, [isMd]);

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        type: 'submit',
        form: 'schedule_message_modal_form',
        label: selectedTime
          ? isRescheduling
            ? 'Reschedule'
            : 'Schedule'
          : 'Send Message',
        disabled:
          isSubmitting ||
          (isIntroCall && !isRescheduling && !isNewExperience
            ? !watchInterest
            : false),
      }}
      customSidebar={{
        title: 'You’re almost done!',
        subtitle:
          'Make sure everything looks right before sending your request! The time will be reserved when the coach accepts the request.',
      }}
      coach={coach}
      coachingSession={coachingSession}
      hasUnlimitedBookingPower={hasUnlimitedBookingPower}
      hideSidebar={!isMd}
      order={order}
      selectedTime={selectedTime}
      sessionLength={sessionLength}
      setOpen={setOpen}
      setSelectedTime={setSelectedTime}
      sessionType={sessionType}
      isReschedule={isRescheduling}
      sidebarLocked
      preventScroll
      {...baseScheduleModalProps}
    >
      <div className="flex w-full grow flex-col overflow-scroll overscroll-contain p-6 md:pb-40">
        {selectedTime ? (
          <>
            <h4 className="text-2xl font-medium text-leland-gray-dark">
              <>
                Add a message{' '}
                {isRescheduling ? (
                  <span className="text-base text-leland-gray-light">
                    (Optional)
                  </span>
                ) : null}
              </>
            </h4>
            <p className="mt-2 max-w-xl text-lg text-leland-gray-light">
              Including a personal message helps the coach know how serious you
              are. Coaches are more likely to accept an intro call if it comes
              with a message.
            </p>
          </>
        ) : (
          <>
            <h4 className="text-2xl font-medium text-leland-gray-dark">
              Send {coach.user.firstName} a message
            </h4>
          </>
        )}
        <div className="mt-6 flex w-full flex-col">
          <form
            id="schedule_message_modal_form"
            onSubmit={handleSubmit(onSubmitWrapper)}
          >
            {submitError ? (
              <p className="py-2 text-leland-red">{submitError}</p>
            ) : null}

            <Textarea
              placeholder={`Hi ${coach.user.firstName}! I'm interested in purchasing coaching and would love to get to know you and learn about your coaching offerings. My goal is...`}
              label={
                <>
                  <span className="text-leland-gray-dark">
                    Include a message
                  </span>{' '}
                  <span className="ml-1 text-leland-gray-extra-light">
                    Optional
                  </span>
                </>
              }
              labelIsHidden={true}
              register={register('message')}
              rows={4}
            />
            {isIntroCall && !isRescheduling && !isNewExperience ? (
              <Radio
                radioClassName="mt-4"
                options={INTRO_INTERESTS_OPTIONS}
                label={
                  <div className="mt-6">
                    <span className="text-leland-gray-dark">
                      Which offering type(s) are you most interested in?
                    </span>
                  </div>
                }
                register={registerIntroCall('interest')}
              />
            ) : null}
          </form>
        </div>
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};
